import { useEffect, useMemo, useState } from "react";

import displayToast from "@common/components/Toast/displayToast";
import useAbortController from "@common/hooks/useAbortController";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import useScoringConfig, { ScoringConfigKeys } from "@main/pages/Release/consumers/useScoringConfig";
import { useAnalyticsDeps } from "@main/pages/Release/Release";
import { useQuery } from "@tanstack/react-query";

export interface MetadataOption {
  key: string;
  enabled: boolean;
}

export default function useTestingCoverageMetadataDimensions() {
  const { analyticsService } = useAnalyticsDeps();
  const { abortSignal } = useAbortController();

  const { selectedWorkspace, selectedApp } = useAppSelectionContext();

  const { scoringConfig, scoringConfigLoading, scoringConfigError, saveScoringConfig, isSaving } = useScoringConfig(
    ScoringConfigKeys.testing_coverage,
  );

  const getMetadataList = async () => {
    if (!selectedWorkspace?.id || !selectedApp?.id) return;

    try {
      const res = await analyticsService.getMetadataKeys(selectedWorkspace.id, selectedApp.id, abortSignal);
      return res.data.metadata_keys;
    } catch (error) {
      if (abortSignal.aborted) return;

      return;
    }
  };

  const {
    data: metadataList,
    isLoading: isMetadataListLoading,
    isError: isMetadataListError,
  } = useQuery([`metadataList`, { selectedApp: selectedApp?.id }], getMetadataList, {
    refetchOnWindowFocus: false,
    cacheTime: Infinity,
  });

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!scoringConfig) return;

    setSelectedKeys(new Set(scoringConfig.config.tracked_metadata_keys ?? []));
  }, [scoringConfig]);

  const metadataOptions: MetadataOption[] = useMemo(() => {
    return (
      metadataList?.map((metadata) => {
        return {
          key: metadata,
          enabled: selectedKeys.has(metadata),
        };
      }) ?? []
    );
  }, [metadataList, selectedKeys]);

  const toggleMetadataOption = (key: string, enabled: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (enabled) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const handleSave = () => {
    const updatedScoringConfig = {
      ...scoringConfig?.config,
      tracked_metadata_keys: Array.from(selectedKeys),
    };

    saveScoringConfig(updatedScoringConfig, {
      onSuccess: () => {
        displayToast({ title: "Saved successfully", content: "Your metadata configuration is saved." });
      },
      onError: () => {
        displayToast({ title: "Something went wrong", content: "Please try again." });
      },
    });
  };

  const isLoading = isMetadataListLoading || scoringConfigLoading;
  const isError = isMetadataListError || scoringConfigError;

  return {
    metadataOptions,
    toggleMetadataOption,
    handleSave,
    isSaving,
    isLoading,
    isError,
  };
}
